import { Check } from 'lucide-react';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'z-50 flex min-w-52 flex-col border border-magenta bg-bg-2 p-1 shadow-[0_0_0_1px_rgb(255_43_214/0.15),0_0_24px_rgb(255_43_214/0.2),0_12px_32px_rgb(0_0_0/0.6)]',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return <DropdownMenuPrimitive.Label className={cn('px-2.5 py-2', className)} {...props} />;
}

export function DropdownMenuItem({ className, ...props }: ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        'flex cursor-pointer items-center gap-2 px-2.5 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-text outline-none select-none data-[highlighted]:bg-magenta/12 data-[highlighted]:text-magenta [&_svg]:size-3.5',
        className,
      )}
      {...props}
    />
  );
}

/** A menu item with a tick box; keeps the menu open so several can be ticked in a row. */
export function DropdownMenuCheckboxItem({
  className,
  children,
  onSelect,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        'flex cursor-pointer items-center gap-2 px-2.5 py-2 text-xs text-text outline-none select-none data-[highlighted]:bg-magenta/12 data-[highlighted]:text-magenta',
        className,
      )}
      onSelect={(e) => {
        e.preventDefault();
        onSelect?.(e);
      }}
      {...props}
    >
      <span
        aria-hidden
        className="grid size-3.5 shrink-0 place-items-center shadow-[inset_0_0_0_1px_var(--color-line-hot)]"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="size-3 text-cyan" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return <DropdownMenuPrimitive.Separator className={cn('my-1 h-px bg-line', className)} {...props} />;
}
